import classNames from 'classnames';
import * as _ from 'lodash-es';
import type Quill from 'quill/core';
import { Delta } from 'quill/core';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import scrollIntoView from 'scroll-into-view-if-needed';
import { useEffectEvent } from 'use-effect-event';
import type { ButtonProps } from '../../components/Button';
import Button, { IconButton } from '../../components/Button';
import Caret from '../../components/Caret';
import Menu, { MenuItem } from '../../components/Menu';
import OverflowMenu from '../../components/OverflowMenu';
import PopOverPrimitive from '../../components/PopOverPrimitive';
import SVGIcon from '../../components/SVGIcon';
import SelectOptionContent from '../../components/SelectOptionContent';
import AttributeHandler from '../../editor/AttributeHandler';
import QuillComponent from '../../editor/QuillComponent';
import { useQuill } from '../../editor/QuillContext';
import EditorToolbar from '../../editor/controls/toolbar/EditorToolbar';
import type {
  MagneticWallFocus,
  MagneticWallType,
} from '../../editor/editorSlice';
import Annotation from '../../editor/formats/Annotation';
import Comment from '../../editor/formats/Comment';
import VersionChangeFormat from '../../editor/formats/VersionChange';
import { useScrollingToAnchorOnLoad } from '../../editor/useAnchorScrollingHandler';
import useHeaderCollapse, {
  getHeaderCollapse,
} from '../../editor/useHeaderCollapse';
import useListCollapse, { getListCollapse } from '../../editor/useListCollapse';
import checkIcon from '../../icons/non-prefilled/16x16/check.svg';
import diffIcon from '../../icons/non-prefilled/16x16/diff.svg';
import overflowMenuIcon from '../../icons/non-prefilled/16x16/overflowMenu.svg';
import retryIcon from '../../icons/non-prefilled/16x16/retry.svg';
import variantIcon from '../../icons/non-prefilled/16x16/variant.svg';
import colorPaintingPaletteIcon from '../../icons/non-prefilled/sidebar/colorPaintingPalette.svg';
import HoverContentPreview from '../HoverContentPreview';
import { getMagneticWallItemContentRects } from '../MagneticWall';
import type { RelativeContentRects } from '../MagneticWallLayout';
import { reduceContentRectsToMap } from '../MagneticWallLayout';
import PostLayout from '../PostLayout';
import getCollapsedElementRect from '../utils/getCollapsedElementRect';
import toRelativeDOMRect from '../utils/toRelativeDOMRect';
import type { PostHistoryMagneticWallVersionChanges } from './PostHistoryMagneticWall';
import * as styles from './VersionChanges.module.scss';
import {
  isVersionColorModeFeatureEnabled,
  useSetVersionColorMode,
  useVersionColorMode,
} from './VersionColorMode';
import type { EditVersionChange } from './calculateVersionChanges';

type VersionChange = EditVersionChange | PostHistoryMagneticWallVersionChanges;

type VersionChangesFocusContextValue = {
  focusedIndex: number;
  focusedVersionChange: VersionChange | null;
  focusById: (id: string | null) => void;
  goToNextChange: () => void;
  goToPreviousChange: () => void;
};

const VersionChangesFocusContext =
  React.createContext<VersionChangesFocusContextValue | null>(null);

export const useVersionChangesFocus = () => {
  const value = useContext(VersionChangesFocusContext);
  if (!value) {
    throw new Error(
      'useVersionChangesFocus must be used within a VersionChangesStateContext.Provider',
    );
  }
  return value;
};

type VersionChangesContextValue = {
  isInitialized: boolean;
  versionChanges: VersionChange[];
  cyclableVersionChangesLength: number;
  updateVersionChanges: (quill: Quill) => void;
};

const VersionChangesContext =
  React.createContext<VersionChangesContextValue | null>(null);

export const useVersionChanges = () => {
  const value = useContext(VersionChangesContext);
  if (!value) {
    throw new Error(
      'useVersionChanges must be used within a VersionChangesContext.Provider',
    );
  }
  return value;
};

type VersionChangeMagneticWallFocusContextValue = {
  magneticWallChangeFocus: MagneticWallFocus | null;
  focusMagneticWallItem: (id: string, type: MagneticWallType) => void;
  blurMagneticWallFocus: () => void;
};

const VersionChangeMagneticWallFocusContext =
  React.createContext<VersionChangeMagneticWallFocusContextValue | null>(null);

export const useVersionChangeMagneticWallFocus = () => {
  const value = useContext(VersionChangeMagneticWallFocusContext);
  if (!value) {
    throw new Error(
      'useVersionChangeMagneticWallFocus must be used within a MagneticWallFocusContext.Provider',
    );
  }
  return value;
};

function isInlineElement(el: Element) {
  const d = getComputedStyle(el).display;
  return d === 'inline' || d.startsWith('inline-');
}

export function getVersionChangeContentRects(
  quill: Quill,
): RelativeContentRects[] {
  const containerRect = quill.container.getBoundingClientRect();
  const collapsedHeaders =
    getHeaderCollapse(quill)?.getCollapsedHeaders() ?? [];
  const collapsedListItems =
    getListCollapse(quill)?.getCollapsedListItems() ?? [];

  return Array.from(
    quill.container.querySelectorAll<HTMLElement>(
      `[${VersionChangeFormat.keyName}]`,
    ),
  ).flatMap((element) => {
    const id = VersionChangeFormat.value(element) as string;

    const collapsedElementRect = getCollapsedElementRect(element, [
      ...collapsedHeaders,
      ...collapsedListItems,
    ]);
    if (collapsedElementRect) {
      return [
        {
          rect: toRelativeDOMRect(collapsedElementRect, containerRect),
          id,
        },
      ];
    }

    const contentRect = element.getClientRects()[0];
    if (!contentRect) return [];

    const lineHeight = parseFloat(window.getComputedStyle(element).lineHeight);

    const finalContentRect = (() => {
      if (element instanceof HTMLParagraphElement) {
        const lastChildRects = element.lastElementChild?.getClientRects();
        const lastChildRect = lastChildRects
          ? Array.from(lastChildRects).at(-1)
          : null;

        // Paragraph rects might not match the last child rect,
        // which is where we show the line break diff.
        // so we attempt to use position it from the last child rect instead.
        // This ensures that the ordering of the diffs is correct
        return lastChildRect
          ? DOMRect.fromRect({
              y: lastChildRect.y + lastChildRect.height - lineHeight,
              x: lastChildRect.x + 12,
              height: lineHeight,
              width: 12,
            })
          : contentRect;
      }

      if (isInlineElement(element)) {
        // Line break diffs are 26px high, inline diffs are 24px high and have a 2px top box shadow
        // to make them visually 26px high. In effect, this means both could have different top values,
        // but still be on the same line. This can cause the ordering of the items in the magnetic wall
        // layout to be incorrect. In order to fix this, we normalize all content rects to top their line.
        return DOMRect.fromRect({
          width: contentRect.width,
          x: contentRect.x,
          y: contentRect.y + contentRect.height - lineHeight,
          height: lineHeight,
        });
      }

      return contentRect;
    })();

    return [
      {
        rect: toRelativeDOMRect(finalContentRect, containerRect),
        id,
      },
    ];
  });
}

const getContentRectsForItemsWithoutContext = (
  itemsWithoutContext: PostHistoryMagneticWallVersionChanges[],
  quill: Quill,
) => {
  const containerRect = quill.container.getBoundingClientRect();

  // This is to place the items without context and
  // without retain at the top of the magnetic wall
  const relativeZeroRect = toRelativeDOMRect(
    DOMRect.fromRect({
      x: containerRect.x,
      y: containerRect.y,
    }),
    containerRect,
  );

  return _.compact(
    itemsWithoutContext.map((item) => {
      if (item.type === 'comment') {
        const retainIndex = new Delta(
          new Delta(item.thread.context).filter((op) => op.retain != null),
        ).length();

        if (retainIndex === 0) {
          return {
            id: item.id,
            rect: relativeZeroRect,
          };
        }

        const [blot] = quill.getLine(Math.min(retainIndex, quill.getLength()));

        if (blot?.domNode instanceof Element) {
          return {
            id: item.id,
            rect: toRelativeDOMRect(
              blot.domNode.getBoundingClientRect(),
              containerRect,
            ),
          };
        }
      }

      return {
        id: item.id,
        rect: relativeZeroRect,
      };
    }),
  );
};

export const getContentRectsForMagneticWallVersionChanges = (
  versionChanges: PostHistoryMagneticWallVersionChanges[],
  quill: Quill,
) => {
  const contentRects = getMagneticWallItemContentRects(quill);

  const idsWithContext = new Set(contentRects.map((rect) => rect.id));
  const itemsWithoutContext = versionChanges.filter(
    (item) => !idsWithContext.has(item.id),
  );
  const contentRectsForItemsWithoutContext =
    getContentRectsForItemsWithoutContext(itemsWithoutContext, quill);

  return [...contentRectsForItemsWithoutContext, ...contentRects];
};

interface MetaProps {
  children: React.ReactNode;
}

const Meta = ({ children }: MetaProps) => {
  return <div className={styles.versionMeta}>{children}</div>;
};

type SelectButtonProps = ButtonProps & {
  open: boolean;
  icon?: string;
};

const SelectButton = React.forwardRef<HTMLElement, SelectButtonProps>(
  ({ children, open, className, icon, ...props }, ref) => {
    return (
      <Button
        ref={ref}
        className={classNames(styles.selectButton, className, {
          [styles.active]: open,
        })}
        {...props}
      >
        <div className={styles.selectButtonContent}>
          {!!icon && <SVGIcon>{icon}</SVGIcon>}
          <div className={styles.selectButtonText}>{children}</div>
        </div>
        <div className={styles.selectButtonCaret}>
          <Caret direction={open ? 'up' : 'down'} size={8} />
        </div>
      </Button>
    );
  },
);

const MODE_OPTIONS = [
  {
    label: 'Diff',
    value: 'diff' as const,
    icon: diffIcon,
    helpText: 'Shows insertions in green and deletions in red',
  },
  {
    label: 'Collaborators',
    value: 'collaborators' as const,
    icon: colorPaintingPaletteIcon,
    helpText: 'Shows changes with colors based on the author',
  },
  {
    label: 'Full Authorship',
    value: 'full' as const,
    icon: variantIcon,
    helpText: 'Shows author colors, including those from previous versions',
  },
];

const ColorModeSelect = () => {
  const [isModeMenuOpen, setIsModeMenuOpen] = useState(false);
  const colorMode = useVersionColorMode();
  const setColorMode = useSetVersionColorMode();

  const selectedModeOption = MODE_OPTIONS.find(
    (option) => option.value === colorMode,
  );

  return (
    <PopOverPrimitive
      trigger="click"
      placement="bottom-start"
      screenPadding={16}
      offset={6}
      onOpenChange={setIsModeMenuOpen}
      open={isModeMenuOpen}
    >
      <PopOverPrimitive.Trigger asChild>
        <SelectButton
          open={isModeMenuOpen}
          onClick={() => setIsModeMenuOpen((isOpen) => !isOpen)}
          icon={selectedModeOption?.icon}
        >
          {selectedModeOption?.label}
        </SelectButton>
      </PopOverPrimitive.Trigger>
      <PopOverPrimitive.Floating>
        <PopOverPrimitive.Content>
          <Menu>
            {MODE_OPTIONS.map((option) => (
              <MenuItem
                wrap
                key={option.value}
                onClick={() => {
                  setColorMode(option.value);
                  setIsModeMenuOpen(false);
                }}
                right={
                  <div
                    className={classNames(styles.checkIconContainer, {
                      [styles.checked]: colorMode === option.value,
                    })}
                  >
                    <SVGIcon className={styles.checkIcon}>{checkIcon}</SVGIcon>
                  </div>
                }
                className={styles.modeSelectOption}
              >
                <SelectOptionContent
                  key={option.value}
                  label={option.label}
                  icon={option.icon}
                  helpText={option.helpText}
                />
              </MenuItem>
            ))}
          </Menu>
        </PopOverPrimitive.Content>
      </PopOverPrimitive.Floating>
    </PopOverPrimitive>
  );
};

interface TopbarProps {
  children?: React.ReactNode;
  overflowMenu?: React.ReactNode;
}

const Topbar = React.memo(({ children, overflowMenu }: TopbarProps) => {
  const [isOverflowMenuOpen, setIsOverflowMenuOpen] = useState(false);
  const { cyclableVersionChangesLength, isInitialized } = useVersionChanges();
  const { focusedIndex, goToNextChange, goToPreviousChange } =
    useVersionChangesFocus();

  const shouldShowCycleBackButton =
    cyclableVersionChangesLength > 1 &&
    focusedIndex >= cyclableVersionChangesLength - 1;

  return (
    <PostLayout.StateBar
      className={styles.barContainer}
      rightContent={
        isVersionColorModeFeatureEnabled() && (
          <div className={styles.bar}>
            <ColorModeSelect />
          </div>
        )
      }
    >
      <div className={styles.bar}>
        <div className={styles.barContent}>
          {children}
          {!!overflowMenu && (
            <OverflowMenu
              placement="bottom-start"
              open={isOverflowMenuOpen}
              onOpenChange={setIsOverflowMenuOpen}
              offset={2}
              button={
                <IconButton
                  aria-label="More options"
                  size="sm"
                  active={isOverflowMenuOpen}
                  variant="muted"
                >
                  {overflowMenuIcon}
                </IconButton>
              }
            >
              {overflowMenu}
            </OverflowMenu>
          )}
        </div>
        <div className={styles.cyclingContainer}>
          {isInitialized && (
            <div className={styles.cyclingLabel}>
              {cyclableVersionChangesLength > 0 ? (
                <>
                  {focusedIndex + 1} / {cyclableVersionChangesLength} changes
                </>
              ) : (
                'No changes'
              )}
            </div>
          )}

          <div className={styles.cyclingButtons}>
            <IconButton
              aria-label="Previous change"
              disabled={focusedIndex <= 0}
              onClick={goToPreviousChange}
              size="sm"
            >
              <Caret block size="regular" direction="up" />
            </IconButton>
            {shouldShowCycleBackButton ? (
              <IconButton
                aria-label="Go back to first change"
                onClick={goToNextChange}
                size="sm"
              >
                {retryIcon}
              </IconButton>
            ) : (
              <IconButton
                aria-label="Next change"
                disabled={focusedIndex >= cyclableVersionChangesLength - 1}
                onClick={goToNextChange}
                size="sm"
              >
                <Caret block size="regular" direction="down" />
              </IconButton>
            )}
          </div>
        </div>
      </div>
    </PostLayout.StateBar>
  );
});

export const moveFocusedClass = (
  root: HTMLElement,
  id: string | null,
  isFromPreviousVersion?: boolean,
) => {
  const focusedElements = new Set<Element>();
  if (id) {
    root
      .querySelectorAll(`[${VersionChangeFormat.keyName}="${id}"]`)
      .forEach((element) => {
        focusedElements.add(element);
        element.classList.add('focused-diff');
      });
  }

  Array.from(
    root.querySelectorAll(
      isFromPreviousVersion
        ? '.focused-diff:not([class*="ql-diff-"])'
        : '.focused-diff[class*="ql-diff-"]',
    ),
  ).forEach((element) => {
    if (!focusedElements.has(element)) {
      element.classList.remove('focused-diff');
    }
  });
};

const scrollVersionChangeIntoView = (root: HTMLElement, id: string) => {
  const element = root.querySelector<HTMLElement>(
    `[${VersionChangeFormat.keyName}="${id}"]`,
  );
  if (element) {
    scrollIntoView(element, { scrollMode: 'if-needed', behavior: 'smooth' });
  }
};

const useScrollToEditVersionChange = () => {
  const { focusedVersionChange } = useVersionChangesFocus();
  const { quill } = useQuill();
  const prevFocusedId = useRef<string | null>(null);

  useEffect(() => {
    if (!quill || !focusedVersionChange) return;

    const isInitialCall = prevFocusedId.current == null;
    prevFocusedId.current = focusedVersionChange.id;

    if (document.location.hash && isInitialCall) {
      // if we have a hash in the URL, useScrollingToAnchorOnLoad will handle the initial scroll
      return;
    }

    if (focusedVersionChange.type === 'edit') {
      scrollVersionChangeIntoView(quill.root, focusedVersionChange.id);
    }
  }, [quill, focusedVersionChange]);
};

const EDITOR_MODULE_CONFIG = {
  hoveredBlotListener: true,
};
interface PostProps {
  content: Delta | undefined;
}

const Post = React.memo(({ content }: PostProps) => {
  const { quill } = useQuill();
  const { focusedVersionChange } = useVersionChangesFocus();
  const { updateVersionChanges } = useVersionChanges();
  const { focusMagneticWallItem } = useVersionChangeMagneticWallFocus();
  const colorMode = useVersionColorMode();

  useHeaderCollapse(quill);
  useListCollapse(quill);
  useScrollingToAnchorOnLoad(quill);
  useScrollToEditVersionChange();

  const focusedId = focusedVersionChange?.id ?? null;

  useEffect(() => {
    if (!quill) return;
    moveFocusedClass(quill.root, focusedId);
  }, [quill, focusedId]);

  useEffect(() => {
    if (quill && focusedId) {
      const listCollapseManager = getListCollapse(quill);
      const headerCollapseManager = getHeaderCollapse(quill);

      const element = quill.root.querySelector(
        `[${VersionChangeFormat.keyName}="${focusedId}"], .${Comment.keyName}-${focusedId}, .${Annotation.keyName}-${focusedId}`,
      );
      const blot = quill.scroll.find(element, true);
      if (!blot) return;

      listCollapseManager?.revealBlot(blot);
      headerCollapseManager?.revealBlot(blot);
    }
  }, [quill, focusedId]);

  if (!content) return null;

  return (
    <AttributeHandler
      onAttributeClick={(attribute, type) => {
        focusMagneticWallItem(attribute.id, type);
      }}
    >
      <div
        className={classNames(styles.historicalPost, `color-mode-${colorMode}`)}
      >
        <EditorToolbar.DisableBoundary>
          <HoverContentPreview>
            <QuillComponent
              containerClassName={styles.editorContainer}
              rootClassName={classNames(styles.editorRoot, 'post-editor')}
              namespace="history"
              onBlotComponentsInitialLoad={updateVersionChanges}
              content={content}
              readOnly
              moduleConfig={EDITOR_MODULE_CONFIG}
            />
          </HoverContentPreview>
        </EditorToolbar.DisableBoundary>
      </div>
    </AttributeHandler>
  );
});

export const useOnVersionChangeClick = (
  callback: (id: string) => void,
  {
    isFromPreviousVersion,
    skip,
  }: {
    isFromPreviousVersion?: boolean;
    skip?: boolean;
  } = {},
) => {
  const { quill } = useQuill();

  const callbackEvent = useEffectEvent(callback);

  useEffect(() => {
    if (!quill || skip) return undefined;

    const listener = (event: MouseEvent) => {
      if (event.target instanceof Node) {
        const element =
          event.target instanceof Element
            ? event.target
            : event.target.parentElement;
        const id = element
          ?.closest(
            isFromPreviousVersion
              ? `[${VersionChangeFormat.keyName}]:not([class*="ql-diff-"])`
              : `[${VersionChangeFormat.keyName}][class*="ql-diff-"]`,
          )
          ?.getAttribute(VersionChangeFormat.keyName);
        if (id) {
          callbackEvent(id);
        }
      }
    };
    quill.root.addEventListener('click', listener);
    return () => {
      quill.root.removeEventListener('click', listener);
    };
  }, [quill, isFromPreviousVersion, skip]);
};

interface VersionChangeState {
  isInitialized: boolean;
  versionChanges: VersionChange[];
  focusedId: string | null;
}

type Action =
  | { type: 'focus'; id: string | null }
  | {
      type: 'setVersionChanges';
      versionChanges: VersionChange[];
    };

const reducer = (
  state: VersionChangeState,
  action: Action,
): VersionChangeState => {
  switch (action.type) {
    case 'focus':
      return { ...state, focusedId: action.id };
    case 'setVersionChanges': {
      return {
        ...state,
        isInitialized: true,
        versionChanges: action.versionChanges,
        focusedId: action.versionChanges.find((v) => v.id === state.focusedId)
          ? state.focusedId
          : null,
      };
    }
    default:
      return state;
  }
};

const initialState: VersionChangeState = {
  isInitialized: false,
  versionChanges: [],
  focusedId: null,
};

interface RootProps {
  children: React.ReactNode;
  allVersionChanges: VersionChange[];
}

const Root = ({ children, allVersionChanges }: RootProps) => {
  const { quill } = useQuill();

  const [magneticWallFocus, setMagneticWallFocus] =
    useState<MagneticWallFocus | null>(null);
  const [{ isInitialized, versionChanges, focusedId }, reducerDispatch] =
    useReducer(reducer, initialState);
  const currentVersionChangesRef = useRef<typeof allVersionChanges>(undefined);

  const cyclableVersionChanges = useMemo(
    () =>
      versionChanges.filter(
        (v) => v.type !== 'edit' || !v.isFromPreviousVersion,
      ),
    [versionChanges],
  );

  const focusById = useCallback((id: string | null) => {
    reducerDispatch({ type: 'focus', id });
  }, []);

  const updateVersionChanges = useCallback(
    (quillInstance: Quill) => {
      if (!allVersionChanges) return;

      const magneticWallVersionChanges = allVersionChanges.filter(
        (v) => v.type === 'comment' || v.type === 'annotation',
      );

      const allRects = [
        ...getContentRectsForMagneticWallVersionChanges(
          magneticWallVersionChanges,
          quillInstance,
        ),
        ...getVersionChangeContentRects(quillInstance),
      ];
      const rectsMap = reduceContentRectsToMap(allRects);

      const sortedVersionChanges = allVersionChanges
        .filter((v) => rectsMap[v.id])
        .toSorted((a, b) => {
          return (rectsMap[a.id]?.top ?? 0) - (rectsMap[b.id]?.top ?? 0);
        });

      currentVersionChangesRef.current = sortedVersionChanges;

      reducerDispatch({
        type: 'setVersionChanges',
        versionChanges: sortedVersionChanges,
      });
    },
    [allVersionChanges],
  );

  useEffect(() => {
    if (
      quill &&
      (!currentVersionChangesRef.current ||
        !_.isEqual(allVersionChanges, currentVersionChangesRef.current))
    ) {
      updateVersionChanges(quill);
    }
  }, [
    updateVersionChanges,
    allVersionChanges,
    quill,
    currentVersionChangesRef,
  ]);

  const focusedIndex = useMemo(
    () =>
      focusedId
        ? cyclableVersionChanges.findIndex((v) => v.id === focusedId)
        : -1,
    [cyclableVersionChanges, focusedId],
  );

  const focusedVersionChange =
    focusedIndex >= 0
      ? (cyclableVersionChanges.at(focusedIndex) ?? null)
      : null;

  useOnVersionChangeClick(focusById);

  const prevFocusedId = useRef<string | null>(null);

  useEffect(() => {
    if (!quill || !focusedId) return;

    const isInitialCall = prevFocusedId.current == null;
    prevFocusedId.current = focusedId;

    if (document.location.hash && isInitialCall) {
      // if we have a hash in the URL, useScrollingToAnchorOnLoad will handle the initial scroll
      return;
    }

    scrollVersionChangeIntoView(quill.root, focusedId);
  }, [quill, focusedId]);

  const magneticWallChangeFocus = useMemo(() => {
    if (magneticWallFocus) return magneticWallFocus;

    if (!focusedVersionChange || focusedVersionChange.type === 'edit') {
      return null;
    }

    return {
      id: focusedVersionChange.id,
      type: focusedVersionChange.type,
      active: true,
      hasExpandIntent: false,
    };
  }, [focusedVersionChange, magneticWallFocus]);

  const focusMagneticWallItem = useCallback(
    (id: string, type: MagneticWallType) => {
      setMagneticWallFocus({
        id,
        type,
        active: true,
        hasExpandIntent: false,
      });

      const versionChange = allVersionChanges?.find(
        (v): v is PostHistoryMagneticWallVersionChanges =>
          v.id === id && v.type === type,
      );

      if (
        (versionChange?.type === 'comment' &&
          versionChange.newCommentIds.size > 0) ||
        (versionChange?.type === 'annotation' && versionChange.isNew)
      ) {
        focusById(versionChange.id);
      }
    },
    [setMagneticWallFocus, focusById, allVersionChanges],
  );

  const versionChangesFocusContextValue =
    useMemo<VersionChangesFocusContextValue>(() => {
      return {
        focusedIndex,
        focusedVersionChange,
        focusById,
        goToNextChange() {
          const newId =
            cyclableVersionChanges[
              (focusedIndex + 1) % cyclableVersionChanges.length
            ]?.id;
          if (!newId) return;
          focusById(newId);
        },
        goToPreviousChange() {
          const newId = cyclableVersionChanges[focusedIndex - 1]?.id;
          if (!newId) return;
          focusById(newId);
        },
      };
    }, [focusedIndex, focusedVersionChange, focusById, cyclableVersionChanges]);

  const magneticWallChangeFocusContextValue =
    useMemo<VersionChangeMagneticWallFocusContextValue>(() => {
      return {
        magneticWallChangeFocus,
        focusMagneticWallItem,
        blurMagneticWallFocus: () => setMagneticWallFocus(null),
      };
    }, [magneticWallChangeFocus, focusMagneticWallItem]);

  const cyclableVersionChangesLength = cyclableVersionChanges.length;

  const versionChangesContextValue = useMemo<VersionChangesContextValue>(() => {
    return {
      isInitialized,
      versionChanges,
      cyclableVersionChangesLength,
      updateVersionChanges,
    };
  }, [
    isInitialized,
    versionChanges,
    cyclableVersionChangesLength,
    updateVersionChanges,
  ]);

  return (
    <VersionChangesContext.Provider value={versionChangesContextValue}>
      <VersionChangesFocusContext.Provider
        value={versionChangesFocusContextValue}
      >
        <VersionChangeMagneticWallFocusContext.Provider
          value={magneticWallChangeFocusContextValue}
        >
          {children}
        </VersionChangeMagneticWallFocusContext.Provider>
      </VersionChangesFocusContext.Provider>
    </VersionChangesContext.Provider>
  );
};

export default {
  Root,
  Topbar,
  Post,
  Meta,
  SelectButton,
};
